import { usePatchTestMutation } from "@/queries/tests/mutations";
import { PublicQuestion } from "@/queries/types";
import styled from "@emotion/styled";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { DSButton } from "@hundred5/design-system";
import { Trans } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { Dispatch } from "react";
import * as amplitude from "../../../amplitude";
import { Icon } from "../../icon";
import { Action, State } from "../reducer";
import {
  getPatchTestMutationOptions,
  preparePatchTestMutationData,
} from "../utils/patch-test-mutation";

interface FooterQuestionNavigationProps {
  question: PublicQuestion;
  questions: PublicQuestion[];
  endsAt: Date | null;
  state: State;
  isAnswerPasteDisabled: boolean;
  dispatch: Dispatch<Action>;
}

export function FooterQuestionNavigation(props: FooterQuestionNavigationProps) {
  const { query, asPath } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const testSlug = query.hashedTestId as string;
  const queryClient = useQueryClient();
  const isPracticeTest = asPath.endsWith("/practice");

  const patchTestMutation = usePatchTestMutation(
    getPatchTestMutationOptions(queryClient),
  );

  const firstQuestion = props.state.activeQuestionIndex === 0;

  const activeQuestionId =
    props.questions[props.state.activeQuestionIndex].test_question_id;
  const activeQuestionAnswer = props.state.answers[activeQuestionId];
  const lastQuestion =
    props.state.activeQuestionIndex === props.questions.length - 1;
  const recordingVideo = props.state.recordingVideo;
  const mutationData = preparePatchTestMutationData(
    jobOpeningSlug,
    testSlug,
    activeQuestionId,
    activeQuestionAnswer,
  );

  return (
    <Container>
      {!firstQuestion ? (
        <StyledButton
          variant="secondary"
          disabled={recordingVideo}
          color="purple-40"
          Icon={<Icon icon={faArrowLeft} color="purple-100" />}
          aria-label="back"
          onClick={() => {
            const index = props.state.activeQuestionIndex - 1;

            if (!isPracticeTest && mutationData) {
              patchTestMutation.mutate(mutationData);
            }

            props.dispatch({
              type: "select_question",
              payload: { index },
            });

            amplitude.trackQuestionSelect(props.state.jobOpeningSlug, index);
          }}
        ></StyledButton>
      ) : null}
      {/* <VideoButton {...props} /> */}

      {!lastQuestion ? (
        <StyledPrimaryButton
          variant="primary"
          size="big"
          disabled={recordingVideo}
          onClick={() => {
            const index = props.state.activeQuestionIndex + 1;

            if (!isPracticeTest && mutationData) {
              patchTestMutation.mutate(mutationData);
            }

            props.dispatch({
              type: "select_question",
              payload: { index },
            });

            amplitude.trackQuestionSelect(props.state.jobOpeningSlug, index);
          }}
        >
          <Trans comment="Next question button">Next question</Trans>
          <Icon icon={faArrowRight} color="white" />
        </StyledPrimaryButton>
      ) : null}

      {lastQuestion ? (
        <StyledPrimaryButton
          disabled={recordingVideo}
          onClick={() => {
            if (!isPracticeTest && mutationData) {
              patchTestMutation.mutate(mutationData, {
                onSuccess: () => {
                  props.dispatch({ type: "submit_test_ask" });
                },
              });
            } else {
              props.dispatch({ type: "submit_test_ask" });
            }
          }}
        >
          <Trans comment="Submit button">Submit test</Trans>
        </StyledPrimaryButton>
      ) : null}
    </Container>
  );
}

//region Styles
const Container = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 8px;
  flex-direction: column-reverse;
  position: relative;

  justify-content: flex-end;
  margin-left: -5px;
  margin-right: -5px;
  margin-bottom: 15px;

  @media (min-width: 768px) {
    width: auto;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 0;
    flex-direction: row;
  }
`;

const StyledPrimaryButton = styled(DSButton)`
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledButton = styled(DSButton)`
  display: flex;
  align-items: center;
  padding-left: 24px;
  padding-right: 24px;

  svg {
    margin-right: 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;
//endregion
